import { config } from 'Utils/config';

const LOADED_IDS = new Set();
const FAILED_IDS = new Map();
const MAX_RETRY_ATTEMPTS = 3;

// Global tracking for verification attempts
const VERIFICATION_TRACKER = new Map(); // action -> { count, lastAttempt, isVerifying }

export function loadRecaptchaScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (LOADED_IDS.has(src)) return resolve();
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      // Script tag in DOM but not yet confirmed loaded — check if grecaptcha is already available
      if (window.grecaptcha) {
        LOADED_IDS.add(src);
        return resolve();
      }
      // Still in-flight (e.g. preloaded by index.js but not finished) — wait for its events
      existingScript.addEventListener('load', () => {
        LOADED_IDS.add(src);
        resolve();
      });
      existingScript.addEventListener('error', () => {
        const attempts = (FAILED_IDS.get(src)?.attempts || 0) + 1;
        FAILED_IDS.set(src, { attempts, timestamp: Date.now() });
        reject(new Error(`Failed to load: ${src} (attempt ${attempts})`));
      });
      return;
    }
    
    const failedInfo = FAILED_IDS.get(src);
    if (failedInfo && failedInfo.attempts >= MAX_RETRY_ATTEMPTS) {
      return reject(new Error(`Max retry attempts reached for: ${src}`));
    }
    
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      LOADED_IDS.add(src);
      FAILED_IDS.delete(src);
      resolve();
    };
    s.onerror = () => {
      const currentAttempts = (failedInfo?.attempts || 0) + 1;
      FAILED_IDS.set(src, {
        attempts: currentAttempts,
        timestamp: Date.now()
      });
      reject(new Error(`Failed to load: ${src} (attempt ${currentAttempts})`));
    };
    document.body.appendChild(s);
  });
}

export function canAttemptVerification(action, maxAttempts = 3) {
  const tracker = VERIFICATION_TRACKER.get(action) || { count: 0, lastAttempt: 0, isVerifying: false };
  
  // If currently verifying, don't allow another attempt
  if (tracker.isVerifying) {
    return false;
  }
  
  // Check if max attempts reached
  if (tracker.count >= maxAttempts) {
    return false;
  }
  
  // Check cooldown period (5 seconds between attempts)
  const now = Date.now();
  if (now - tracker.lastAttempt < 5000) {
    return false;
  }
  
  return true;
}

export function startVerification(action) {
  const tracker = VERIFICATION_TRACKER.get(action) || { count: 0, lastAttempt: 0, isVerifying: false };
  VERIFICATION_TRACKER.set(action, {
    count: tracker.count + 1,
    lastAttempt: Date.now(),
    isVerifying: true
  });
}

export function endVerification(action) {
  const tracker = VERIFICATION_TRACKER.get(action) || { count: 0, lastAttempt: 0, isVerifying: false };
  VERIFICATION_TRACKER.set(action, {
    ...tracker,
    isVerifying: false
  });
}

/**
 * Get a reCAPTCHA v3 token for the given action.
 * Ensures the script is loaded before executing.
 * Returns null if the site key is not configured.
 * Rejects if grecaptcha.execute fails.
 */
export async function getRecaptchaToken(action) {
  if (!config.recaptchaV3DevSiteKey) return null;

  const src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(config.recaptchaV3DevSiteKey)}`;
  await loadRecaptchaScriptOnce(src);

  if (!window.grecaptcha) return null;

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha.execute(config.recaptchaV3DevSiteKey, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}
